import React, { useEffect, useMemo, useState } from 'react';
import { Avatar, Box, Button, Link, Paper, Rating, Typography } from '@mui/material';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import GoogleLogo from './common/GoogleLogo';
import { businessService, GoogleReview, GoogleReviewsResult } from '../services/businessService';

// Google reviews on a listing, collected by the reviews worker and kept over time:
// the 5 newest (with text first), the star breakdown and the average rating per month.
// Shows nothing until reviews have been collected for the listing.

const faDate = (iso: string | null) => {
    if (!iso) return '';
    try {
        return new Intl.DateTimeFormat('fa-IR', { year: 'numeric', month: 'long' }).format(new Date(iso));
    } catch {
        return iso.slice(0, 7);
    }
};
const faMonth = (month: string) => faDate(`${month}-15T00:00:00Z`);

const ReviewItem: React.FC<{ r: GoogleReview; first: boolean }> = ({ r, first }) => {
    const [open, setOpen] = useState(false);
    const long = r.text.length > 320;
    return (
        <Box sx={{ borderTop: first ? 'none' : '1px solid #edf0f4', pt: first ? 0 : 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.75 }}>
                <Avatar src={r.author.photo || undefined} alt="" sx={{ width: 32, height: 32 }} imgProps={{ referrerPolicy: 'no-referrer' }}>
                    {r.author.name.slice(0, 1)}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                    {r.author.url
                        ? <Link href={r.author.url} target="_blank" rel="noopener noreferrer nofollow" sx={{ fontWeight: 600, fontSize: 14 }} dir="auto">{r.author.name}</Link>
                        : <Typography sx={{ fontWeight: 600, fontSize: 14 }} dir="auto">{r.author.name}</Typography>}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Rating value={r.rating} readOnly size="small" />
                        <Typography variant="caption" color="text.secondary">{faDate(r.publishTime)}</Typography>
                    </Box>
                </Box>
            </Box>
            {r.text && (
                <Typography variant="body2" sx={{ color: '#4a5568', whiteSpace: 'pre-line', textAlign: 'start' }} dir="auto">
                    {long && !open ? `${r.text.slice(0, 320)}…` : r.text}
                    {long && (
                        <Link component="button" onClick={() => setOpen(!open)} sx={{ mx: 0.5, fontSize: 13 }}>
                            {open ? 'کمتر' : 'بیشتر'}
                        </Link>
                    )}
                </Typography>
            )}
            {r.ownerReply && (
                <Box sx={{ mt: 1, mr: 2, p: 1.25, bgcolor: '#f6f8fb', borderRadius: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#2854a1', display: 'block' }}>پاسخ کسب‌وکار</Typography>
                    <Typography variant="body2" sx={{ color: '#4a5568', whiteSpace: 'pre-line', textAlign: 'start' }} dir="auto">{r.ownerReply}</Typography>
                </Box>
            )}
        </Box>
    );
};

const GoogleReviews: React.FC<{ businessId: string; paperSx?: object }> = ({ businessId, paperSx }) => {
    const [data, setData] = useState<GoogleReviewsResult | null>(null);
    const [showChart, setShowChart] = useState(false);

    useEffect(() => {
        let alive = true;
        businessService.getGoogleReviews(businessId)
            .then(d => { if (alive) setData(d); })
            .catch(() => { if (alive) setData(null); });
        return () => { alive = false; };
    }, [businessId]);

    const totalStars = useMemo(
        () => (data ? Object.values(data.distribution || {}).reduce((t, n) => t + n, 0) : 0),
        [data],
    );

    if (!data || !data.collected || data.reviews.length === 0) return null;
    const history = data.history || [];

    return (
        <Paper sx={{ ...(paperSx || {}), direction: 'rtl' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#2854a1', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GoogleLogo sx={{ fontSize: 20 }} />
                    نظرات گوگل
                    {data.rating ? (
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mr: 1, fontWeight: 500, color: '#4a5568', fontSize: 14 }}>
                            <Rating value={data.rating} precision={0.1} readOnly size="small" />
                            {data.rating.toFixed(1)}{data.count ? ` (${data.count})` : ''}
                        </Box>
                    ) : null}
                </Typography>
                {history.length >= 2 && (
                    <Button variant="text" size="small" onClick={() => setShowChart(!showChart)}>
                        {showChart ? 'بستن نمودار' : 'روند امتیاز'}
                    </Button>
                )}
            </Box>

            {showChart && (
                <Box sx={{ mt: 2, display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Box sx={{ flex: '0 0 200px', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {[5, 4, 3, 2, 1].map(star => {
                            const n = data.distribution?.[String(star)] || 0;
                            const pct = totalStars ? Math.round((n / totalStars) * 100) : 0;
                            return (
                                <Box key={star} sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 13 }}>
                                    <Box component="span" sx={{ width: 14 }}>{star}</Box>
                                    <Box sx={{ flex: 1, height: 8, bgcolor: '#edf0f4', borderRadius: 4, overflow: 'hidden' }}>
                                        <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: '#f5b400' }} />
                                    </Box>
                                    <Box component="span" sx={{ width: 28, color: 'text.secondary' }}>{n}</Box>
                                </Box>
                            );
                        })}
                    </Box>
                    <Box sx={{ flex: '1 1 280px', height: 180, direction: 'ltr' }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={history} margin={{ top: 8, right: 12, bottom: 0, left: -20 }}>
                                <CartesianGrid stroke="#edf0f4" vertical={false} />
                                <XAxis dataKey="month" tickFormatter={faMonth} tick={{ fontSize: 11 }} minTickGap={24} />
                                <YAxis domain={[1, 5]} ticks={[1, 2, 3, 4, 5]} tick={{ fontSize: 11 }} />
                                <Tooltip
                                    labelFormatter={(m: any) => faMonth(String(m))}
                                    formatter={(v: any, _n: any, item: any) => [`${v} (${item?.payload?.count} نظر)`, 'میانگین']}
                                />
                                <Line type="monotone" dataKey="average" stroke="#2854a1" strokeWidth={2} dot={{ r: 3 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ width: '100%' }}>
                        بر اساس {data.collected} نظری که از گوگل جمع‌آوری شده است.
                    </Typography>
                </Box>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                {data.reviews.map((r, i) => <ReviewItem key={r.id} r={r} first={i === 0} />)}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mt: 2, pt: 1.5, borderTop: '1px solid #edf0f4' }}>
                {data.googleMapsUri
                    ? <Link href={data.googleMapsUri} target="_blank" rel="noopener noreferrer" sx={{ fontSize: 14 }}>مشاهده همه نظرات در گوگل</Link>
                    : <span />}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }} dir="ltr">
                    Reviews from <GoogleLogo sx={{ fontSize: 14 }} /> Google
                </Typography>
            </Box>
        </Paper>
    );
};

export default GoogleReviews;
